import {RefObject, useLayoutEffect} from 'react';

import {globalWindow} from '../../libs/shared/helpers/src/browser/browserHelpers';
import {isHtmlOrSvgElement} from '../../libs/shared/helpers/src/browser/domHelpers';

export type InputFocusCallback = () => void;
export type InputFocusUnsubscriber = () => void;

const defaultInputFocusTimeout = 3000;
const noop = () => undefined;

/**
 * Calls back when the window loses focus, an input-like element receives focus, or a timeout occurs.
 *
 * Used as a safeguard to to ensure keyboard events that are intended for text input don't inadvertently
 * trigger keyboard shortcuts.
 */
export function subscribeToInputFocus(
  document: Document,
  callback: InputFocusCallback,
  timeout = defaultInputFocusTimeout,
): InputFocusUnsubscriber {
  const window = document.defaultView;
  if (!window) {
    return noop;
  }

  const listener = () => {
    removeListeners();
    callback();
  };

  const removeListeners = () => {
    window.clearTimeout(timeoutHandle);
    window.removeEventListener('blur', listener);
    document.removeEventListener('focusin', listener);
  };

  const timeoutHandle = window.setTimeout(listener, timeout);
  window.addEventListener('blur', listener, {once: true});
  document.addEventListener('focusin', listener, {once: true});

  return removeListeners;
}

/** Blurs the currently active element upon mount, and re-focuses it upon unmount. */
export function useSaveAndRestoreFocusEffect(contextRef: RefObject<Node | null>) {
  useLayoutEffect(() => {
    const container = contextRef.current;
    if (!container) {
      return undefined;
    }

    const document = container.ownerDocument;
    if (!document) {
      return undefined;
    }

    // Check if we have a focused element.
    const {activeElement} = document;
    if (!activeElement || !isHtmlOrSvgElement(activeElement)) {
      return undefined;
    }

    // If so, remember it and blur.
    const prevActiveElement = activeElement;
    prevActiveElement.blur();

    return () => {
      // If the document's contents are gone, nothing to do. (Yes, this happens.)
      const {body} = document;
      if (!body) {
        return;
      }

      // If the previously selected element is not visible, nothing to do.
      if (!body.contains(prevActiveElement)) {
        return;
      }

      // Otherwise, focus it.
      globalWindow.setInstrumentedTimeout(() => prevActiveElement.focus(), 0);
    };
  }, [contextRef]);
}
